import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { shortDate, Pill } from "@/lib/money";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, Search, ShieldAlert, ShieldCheck, AlertTriangle } from "lucide-react";

const DECISION_STYLE = {
  clear: "bg-emerald-50 text-emerald-700 border-emerald-200",
  potential_match: "bg-amber-50 text-amber-700 border-amber-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
  unavailable: "bg-slate-200 text-slate-700 border-slate-300",
};
const DECISION_LABEL = {
  clear: "Clear",
  potential_match: "Possible match",
  blocked: "Blocked",
  unavailable: "Could not screen",
};

export default function Compliance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [screenings, setScreenings] = useState([]);
  const [q, setQ] = useState("");
  const [dob, setDob] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewNote, setReviewNote] = useState("");

  const load = async () => {
    try {
      const [s, sc] = await Promise.all([
        api.get("/compliance/sanctions-status"),
        api.get("/compliance/screenings"),
      ]);
      setStatus(s.data);
      setScreenings(sc.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const runScreen = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await api.post("/compliance/screen", {
        full_name: q.trim(),
        ...(dob ? { date_of_birth: dob } : {}),
      });
      setResult(r.data.result);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/compliance/refresh-sanctions");
      toast.success(`${r.data.entries.toLocaleString()} entries loaded`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitReview = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/compliance/screenings/${review.id}/review`,
                     { outcome, note: reviewNote });
      toast.success("Review recorded");
      setReview(null); setReviewNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const open = screenings.filter((s) => s.review_status === "open");

  return (
    <Layout>
      <div className="fade-in space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Compliance
            </h1>
            <p className="text-sm text-slate-500">
              Sanctions screening against the OFAC and Canadian consolidated lists.
            </p>
          </div>
          {isAdmin && (
            <Button onClick={refresh} disabled={busy} variant="outline" data-testid="refresh-lists-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh lists
            </Button>
          )}
        </div>

        {/* If the list is not loaded or is stale, that is the most important thing
            on this page - a screen that cannot run must never look like a pass. */}
        {status && (!status.loaded || status.stale) && (
          <Card className={"rounded-xl " + (status.loaded ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50")}>
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className={"h-5 w-5 shrink-0 " + (status.loaded ? "text-amber-700" : "text-red-700")} />
              <div className="text-sm">
                <div className={"font-semibold " + (status.loaded ? "text-amber-900" : "text-red-900")}>
                  {status.loaded
                    ? `The sanctions list is ${status.age_days} days old`
                    : "No sanctions list is loaded"}
                </div>
                <p className={status.loaded ? "text-amber-800" : "text-red-800"}>
                  {status.loaded
                    ? "Lists are refreshed weekly. An out-of-date list can miss a recent designation."
                    : "Screening cannot run. Applicants are referred rather than cleared until a list is loaded."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["List entries", status ? (status.entries || 0).toLocaleString() : "—",
             status ? `version ${String(status.version || "").slice(0, 8)}` : ""],
            ["OFAC SDN", status ? (status.by_source?.["OFAC-SDN"] || 0).toLocaleString() : "—", "US Treasury"],
            ["Canada SEMA", status ? (status.by_source?.["CA-SEMA"] || 0).toLocaleString() : "—", "Consolidated list"],
            ["Open reviews", String(open.length), open.length ? "need a decision" : "none outstanding"],
          ].map(([k, v, sub]) => (
            <Card key={k} className="rounded-xl border-slate-200"><CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">{k}</div>
              <div className="text-xl font-semibold mt-1 tabular-nums">{v}</div>
              <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
            </CardContent></Card>
          ))}
        </div>

        <Tabs defaultValue="screen">
          <TabsList>
            <TabsTrigger value="screen" data-testid="tab-screen">Screen a name</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              History ({screenings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="screen">
            <Card className="rounded-xl border-slate-200"><CardContent className="p-5 space-y-4">
              <form onSubmit={runScreen} className="flex gap-2 flex-wrap items-end">
                <div className="flex-1 min-w-[220px]">
                  <Label>Full name</Label>
                  <Input value={q} onChange={(e) => setQ(e.target.value)}
                         placeholder="As it appears on their identity document"
                         data-testid="screen-name" />
                </div>
                <div>
                  <Label>Date of birth (optional)</Label>
                  <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                         data-testid="screen-dob" />
                </div>
                <Button type="submit" disabled={busy || q.trim().length < 2}
                        className="bg-blue-900 hover:bg-blue-800 h-10" data-testid="run-screen-btn">
                  <Search className="h-4 w-4 mr-1" /> Screen
                </Button>
              </form>

              {result && (
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    {result.decision === "clear"
                      ? <ShieldCheck className="h-6 w-6 text-emerald-700" />
                      : <ShieldAlert className="h-6 w-6 text-red-700" />}
                    <div>
                      <Pill className={DECISION_STYLE[result.decision]}>
                        {DECISION_LABEL[result.decision] || result.decision}
                      </Pill>
                      <span className="ml-2 text-sm text-slate-500 tabular-nums">
                        score {result.score}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{result.reason}</p>
                  <p className="text-xs text-slate-400">
                    {result.candidates_examined} candidate entries examined · thresholds
                    refer {result.thresholds?.refer_at}, block {result.thresholds?.block_at}
                  </p>
                  {[["matches", "Matches"], ["suppressed_by_dob", "Namesakes set aside on date of birth"]]
                    .map(([key, title]) => (
                      (result[key] || []).length > 0 && (
                        <div key={key}>
                          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{title}</div>
                          <div className="space-y-1">
                            {result[key].map((m, i) => (
                              <div key={i} className="flex items-start justify-between gap-3 text-sm
                                                      border border-slate-200 rounded-lg px-3 py-2">
                                <div className="min-w-0">
                                  <div className="font-medium truncate">{m.name}</div>
                                  <div className="text-xs text-slate-500">
                                    {m.source} · {m.entry_type}
                                    {m.program ? ` · ${m.program}` : ""}
                                    {m.dob_year ? ` · born ${m.dob_year}` : ""}
                                    {m.is_alias ? " · alias" : ""}
                                    {m.note ? ` · ${m.note}` : ""}
                                  </div>
                                </div>
                                <span className="tabular-nums font-semibold shrink-0">{m.score}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="rounded-xl border-slate-200"><CardContent className="p-0">
              {screenings.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-500">No screenings recorded yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2">Name screened</th>
                        <th className="text-left px-4 py-2">Outcome</th>
                        <th className="text-right px-4 py-2">Score</th>
                        <th className="text-left px-4 py-2">When</th>
                        <th className="text-left px-4 py-2">Review</th>
                        {isAdmin && <th className="px-4 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {screenings.slice(0, 80).map((s) => (
                        <tr key={s.id} className="border-t border-slate-100">
                          <td className="px-4 py-2">
                            {s.subject_name}
                            {s.matches?.[0] && (
                              <div className="text-xs text-slate-500 truncate max-w-[260px]">
                                vs {s.matches[0].name}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <Pill className={DECISION_STYLE[s.decision]}>
                              {DECISION_LABEL[s.decision] || s.decision}
                            </Pill>
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">{s.score}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                            {shortDate(s.screened_at)}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {s.review_status === "n/a" ? <span className="text-slate-400">—</span>
                              : s.review_status === "open" ? <span className="text-amber-700 font-medium">Open</span>
                              : <span className="text-slate-600">
                                  {s.review_status} {s.reviewed_by ? `· ${s.reviewed_by}` : ""}
                                </span>}
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-2 text-right">
                              {s.review_status === "open" && (
                                <Button variant="outline" size="sm" className="h-8 px-2 text-xs"
                                        onClick={() => setReview(s)}
                                        data-testid={`review-${s.id}`}>Review</Button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!review} onOpenChange={(o) => { if (!o) { setReview(null); setReviewNote(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve this screening</DialogTitle>
            <DialogDescription>
              {review && (
                <>
                  <b>{review.subject_name}</b> scored {review.score} against{" "}
                  <b>{review.matches?.[0]?.name}</b> on {review.matches?.[0]?.list_name}.
                  Decide whether this is the same person.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Note (recorded against the decision)</Label>
            <Input value={reviewNote} onChange={(e) => setReviewNote(e.target.value)}
                   placeholder="What did you check?" data-testid="review-note" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => submitReview("false_positive")}
                    disabled={busy || reviewNote.trim().length < 3}
                    data-testid="mark-false-positive">Not the same person</Button>
            <Button onClick={() => submitReview("confirmed")}
                    disabled={busy || reviewNote.trim().length < 3}
                    className="bg-red-700 hover:bg-red-800"
                    data-testid="mark-confirmed">Confirm the match</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
